/*
  The MIT License

  Copyright (c) 2017-2019 EclipseSource Munich
  https://github.com/eclipsesource/jsonforms

  Copyright (c) 2020 headwire.com, Inc
  https://github.com/headwirecom/jsonforms-react-spectrum-renderers

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
import React, { useCallback } from 'react';
import { ArrayControlProps, createDefaultValue } from '@jsonforms/core';
import { ResolvedJsonFormsDispatch } from '@jsonforms/react';
import { Button, Flex, Heading, View } from '@adobe/react-spectrum';
import SpectrumArrayItem from './SpectrumArrayItem';

export const SpectrumArrayControl = ({
  data,
  label,
  path,
  schema,
  addItem,
  removeItems,
  uischema,
  uischemas,
  renderers,
  visible,
}: ArrayControlProps) => {
  const handleRemoveItem = useCallback(
    (p: string, value: any) => () => {
      removeItems(p, [value])();
    },
    [removeItems]
  )

  const options = uischema?.options;
  const customLabel = options?.label;
  const limit = options?.limit;
  const addMoreButton = options?.addMoreButton;
  /** condition to hide/show delete option for first element  */
  const hideDeleteForPrimary = options?.hideDeleteForPrimary;
  /** condition to hide/show Items when there is no data  */
  const hideElementOnNodata = options?.hideElementOnNodata;
  /** condition to hide/show Label  when there is no data  */
  const showLabelOnNodata = options?.showLabelOnNodata;

  //If passed as true, the delete confirmation modal will not be shown
  const hideDeleteConfirmation = options?.hideDeleteConfirmation || false;

  //If passed as true, hides the add more button
  const hideAddMore = options?.hideAddMore || false;

  //If passed as true, hides the add more button when limit has reached
  const hideAddMoreOnLimit = options?.hideAddMoreOnLimit || false;

  const isAddMoreDisabled = limit && data && data.length >= limit

  // Determines if the add more needs to be hidden if the limit has reached
  const isAddMoreHiddenOnLimit = hideAddMoreOnLimit && limit && data && data.length >= limit

  return (
    <View
      isHidden={visible === undefined || visible === null ? false : !visible}
    >
      {(data && data.length) || showLabelOnNodata ? (<Flex direction='row' justifyContent='space-between'>
        {customLabel ? <ResolvedJsonFormsDispatch
          schema={{}}
          uischema={customLabel}
          path={''}
          key={''}
          renderers={renderers}
        /> : <Heading level={4}>{label}</Heading>}

      </Flex>) : ""}
      <Flex direction='column' gap='size-100'>
        {data && data.length ? (
          Array.from(Array(data.length)).map((_, index) => {
            return (
              <SpectrumArrayItem
                hideDeleteConfirmation={hideDeleteConfirmation}
                showDelete={hideDeleteForPrimary ? index !== 0 : true}
                index={index}
                path={path}
                schema={schema}
                removeItem={handleRemoveItem}
                uischema={uischema}
                uischemas={uischemas}
                renderers={renderers}
                key={index}
              ></SpectrumArrayItem>
            );
          })
        ) : hideElementOnNodata ? "" : <SpectrumArrayItem
          hideDeleteConfirmation={hideDeleteConfirmation}
          showDelete={hideDeleteForPrimary ? false : true}
          index={0}
          path={path}
          schema={schema}
          removeItem={handleRemoveItem}
          uischema={uischema}
          uischemas={uischemas}
          renderers={renderers}
          key={0}
        ></SpectrumArrayItem>}
      </Flex>
      {
        !hideAddMore && !isAddMoreHiddenOnLimit && <Flex>
          <Button
            variant='primary'
            alignSelf='center'
            isDisabled={isAddMoreDisabled}
            onPress={addItem(path, createDefaultValue(schema))}
            {...addMoreButton?.spectrumProps}
            UNSAFE_style={{ margin: "16px 0", ...addMoreButton?.style }}
          >
            {addMoreButton?.content ? (
              <ResolvedJsonFormsDispatch
                schema={{}}
                uischema={addMoreButton?.content}
                path={''}
                key={''}
                renderers={renderers}
              />
            ) : (
              '+'
            )}
          </Button>
        </Flex>
      }
    </View>
  );
};

export default SpectrumArrayControl;